import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Popover } from 'antd';
import Tooltip from '../../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../store/store'
import {
  appendRawContents,
  hydrateVariables,
  rawContentHasEntity,
  rawContentToText,
} from '../../../utils/predefinedValues'
import { getActiveField } from '../../../utils/activeFields'

const PREVIEW_LENGTH = 70

// Valeurs prédéfinies d'un champ de texte. Contrairement à celles d'une
// variable texte custom, ce sont des contenus riches : les variables et les
// images qu'elles portent sont recopiées telles quelles dans le champ, donc
// une variable insérée reste vivante et suit ses données.
const TextPredefinedValuesPicker = ({ boxId, zoom }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // Une variable peut vivre dans un masque (en-tête / pied de page) : la box
  // n'est alors pas dans `boxes`.
  const predefinedValues = useStore(({ boxes, masks }) =>
    [...boxes, ...masks.map((m) => m.boxes).flat()].find((b) => b.id === boxId)?.predefinedValues
  )
  const updateBox = useStore(({ updateBox }) => updateBox)
  const bumpDocumentContentSyncTick = useStore(({ bumpDocumentContentSyncTick }) => bumpDocumentContentSyncTick)

  const options = useMemo(() => (predefinedValues ?? [])
    .map((value, index) => ({ value, index }))
    .filter(({ value }) => typeof value === 'string' && (
      rawContentToText(value) !== '' || rawContentHasEntity(value, 'IMAGE')
    )), [predefinedValues])

  // L'aperçu est une liste virtualisée : en défilant, la ligne peut être
  // démontée et le popover resterait à flotter loin de son bouton. En capture,
  // le défilement d'un conteneur interne ne remontant pas jusqu'à window.
  useEffect(() => {
    if (!open) return undefined
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    return () => window.removeEventListener('scroll', close, true)
  }, [open])

  // Le popover reste ouvert : plusieurs valeurs peuvent s'enchaîner, et le
  // curseur regagne le champ après chaque insertion. Le fermer rendrait le
  // focus au bouton et se battrait avec cette reprise du curseur.
  const handleApply = useCallback((rawValue) => {
    // Champ à l'écran : insertion à la position du curseur, sans retour à la
    // ligne — l'utilisateur place son curseur là où il veut la valeur.
    const field = getActiveField(boxId)
    if (field?.insertRawContent) {
      field.insertRawContent(rawValue)
      return
    }

    // Repli quand l'éditeur n'est pas monté (ligne du formulaire non rendue par
    // la liste virtualisée) : ajout en fin de contenu.
    const { variables, currentDocument } = useStore.getState()
    updateBox(boxId, (b) => {
      const appended = appendRawContents({
        editorState: b.content?.editorState,
        rawValues: [rawValue],
      })
      if (!appended) return
      // Le texte figé dans la valeur est le nom des variables : sans cette
      // traduction il resterait affiché jusqu'au prochain chargement.
      const nextEditorState = hydrateVariables({
        editorState: appended,
        variables,
        isTemplate: currentDocument.type === 'template',
      })
      b.content.editorState = nextEditorState
      // Hauteur laissée telle quelle : une box texte la porte en pixels, elle
      // est remesurée à la déselection, exactement comme après une saisie.
    })
    bumpDocumentContentSyncTick()
  }, [boxId, updateBox, bumpDocumentContentSyncTick])

  if (options.length === 0) return null

  const label = ({ value }) => {
    const text = rawContentToText(value)
    const hasImage = rawContentHasEntity(value, 'IMAGE')
    const preview = text.length > PREVIEW_LENGTH ? `${text.slice(0, PREVIEW_LENGTH)}…` : text
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {hasImage && (
          <FontAwesomeIcon
            icon={icon({ name: 'image', style: 'solid' })}
            style={{ color: '#5499DE' }}
          />
        )}
        <span>{preview || t('predefined-value-image-only')}</span>
      </span>
    )
  }

  // stopPropagation : le contenu est porté par un portail mais les évènements
  // React remontent l'arbre des composants, jusqu'aux poignées de sélection de
  // la ligne du formulaire.
  // Un clic applique : pas de bouton de validation, la valeur part directement
  // dans le champ.
  const content = (
    <div
      style={{ minWidth: 220, maxWidth: 340, maxHeight: 260, overflowY: 'auto' }}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((option) => (
        <button
          key={option.index}
          type="button"
          onClick={() => handleApply(option.value)}
          style={valueButtonStyle}
        >
          {label(option)}
        </button>
      ))}
    </div>
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger='click'
      placement='rightTop'
      title={t('apply-predefined-values')}
      content={content}
      overlayStyle={{ zoom: `${zoom ?? 100}%` }}
    >
      {/* span porteur du ref : FontAwesomeIcon rend un <svg> et le Tooltip
          imbriqué réclame déjà son propre enfant. */}
      <span
        style={{ display: 'inline-flex' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title={t('apply-predefined-values')} mouseLeaveDelay="0">
          <FontAwesomeIcon
            icon={icon({ name: 'list-check', style: 'solid' })}
            style={{ marginLeft: 6, color: '#5499DE', cursor: 'pointer' }}
          />
        </Tooltip>
      </span>
    </Popover>
  )
}

const valueButtonStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '6px 8px',
  border: 'none',
  borderRadius: 4,
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 13,
  wordBreak: 'break-word',
}

export default TextPredefinedValuesPicker
